/**
 * <be-popup>: timed welcome offer. Opens after the visitor has spent `data-delay` seconds
 * on the page (visible time only), never while a drawer or menu is open, and remembers the
 * visitor's choice in localStorage (closed = snoozed, signed up = never again).
 */
import { lockScroll } from '@be/core';

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
};
const write = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable: the popup simply may show again */
  }
};

class BePopup extends HTMLElement {
  #timer = 0;
  #shown = false;
  #ac = new AbortController();

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    const { signal } = this.#ac;
    this.key = this.dataset.key || 'be-offer';
    const done = this.querySelector('[data-done]');
    const failed = this.querySelector('[aria-invalid="true"]');

    this.dialog.addEventListener('close', () => this.#onClose(), { signal });
    this.dialog.addEventListener('click', (event) => this.#onClick(event), { signal });

    if (done) {
      write(this.key, { claimed: true });
      this.open();
      return;
    }
    if (failed) return this.open();
    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:section:select', (e) => this.contains(/** @type {Node} */ (e.target)) && this.open(), { signal });
      document.addEventListener('shopify:section:deselect', () => this.close(), { signal });
      return;
    }
    const state = read(this.key);
    if (state?.claimed || (state?.snoozedUntil && state.snoozedUntil > Date.now())) return;
    this.#countdown(Number(this.dataset.delay || 60));
  }

  disconnectedCallback() {
    this.#ac.abort();
    clearInterval(this.#timer);
    lockScroll(this, false);
  }

  /** Counts only seconds the tab is visible, then waits for any open drawer to close. */
  #countdown(seconds) {
    let left = seconds;
    this.#timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      left -= 1;
      if (left > 0) return;
      const busy = document.documentElement.hasAttribute('scroll-lock') || document.querySelector('dialog[open]');
      if (busy) return;
      clearInterval(this.#timer);
      this.open();
    }, 1000);
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    this.#shown = true;
    lockScroll(this, true);
    const field = this.dialog.querySelector('input[type="email"]');
    if (field instanceof HTMLElement && !('ontouchstart' in window)) field.focus({ preventScroll: true });
  }

  /** Closes the dialog; state is saved here because the native close event is async. */
  close() {
    if (this.dialog?.open) this.dialog.close();
    this.#onClose();
  }

  #onClose() {
    if (!this.#shown) return;
    this.#shown = false;
    lockScroll(this, false);
    const state = read(this.key);
    if (state?.claimed) return;
    const days = Number(this.dataset.snooze || 14);
    write(this.key, { snoozedUntil: Date.now() + days * 86400000 });
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target || !this.dialog) return;
    if (target === this.dialog || target.closest('[data-popup-close]')) {
      this.close();
      return;
    }
    const copy = target.closest('[data-popup-copy]');
    if (copy instanceof HTMLElement) {
      const code = this.querySelector('[data-popup-code]')?.textContent?.trim() || '';
      navigator.clipboard?.writeText(code).then(() => {
        copy.textContent = copy.dataset.copied || copy.textContent;
      });
      write(this.key, { claimed: true });
      return;
    }
    if (target.closest('[data-popup-claim]')) write(this.key, { claimed: true });
  }
}

if (!customElements.get('be-popup')) customElements.define('be-popup', BePopup);